import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { formatSom } from 'components/entitlements/billing/billingHelpers';
import { entitlementsApi, usePaymentMethods } from 'modules/entitlements';
import { PaymentMethodPicker } from 'components/entitlements/billing/PaymentMethodPicker';
import { CardTransferPanel } from 'components/entitlements/billing/CardTransferPanel';
import type { Plan } from 'modules/entitlements/api';
import { isOnlineMethod, type PaymentMethod } from 'modules/payments/methods';
import { GetWallet } from 'modules/coins/api';
import { COIN_REDEMPTION_CAP_PCT } from 'modules/coins/constants';
import { IS_IO } from 'lib/site';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  plan: Plan | null;
}

/** Activation happens via the provider's webhook — not the browser return. */
export const CheckoutModal: React.FC<CheckoutModalProps> = ({ open, onClose, plan }) => {
  const { t } = useTranslation();
  const [chosenMethod, setMethod] = useState<PaymentMethod | null>(null);
  const [applyCoins, setApplyCoins] = useState(false);
  const [promocode, setPromocode] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: wallet } = useQuery({
    queryKey: ['coins', 'wallet'],
    queryFn: async () => (await GetWallet()).data,
    enabled: open && Boolean(plan),
    staleTime: 30_000,
  });

  const methods = usePaymentMethods(open && Boolean(plan), 'plan');

  if (!plan) return null;

  const method = methods.pick(chosenMethod);

  const gross = plan.monthlyPrice;

  const balance = wallet?.balance ?? 0;
  // Coins can cover up to the cap (now 100%) — mirror of the server-side math in
  // PaymentsService.createCheckout / CoinsService.computeApplicable.
  const cappedDiscount = Math.floor((COIN_REDEMPTION_CAP_PCT / 100) * gross);
  const coinsApplicable = applyCoins ? Math.max(0, Math.min(balance, cappedDiscount, gross)) : 0;
  const charged = gross - coinsApplicable;
  // Coins settle the whole invoice → no provider payment, activate directly.
  const fullyCovered = charged <= 0 && coinsApplicable > 0;
  // A card transfer is paid outside the platform at the full price and activated by an admin,
  // so promo codes, referral codes and a partial coin discount do not apply to it.
  const cardTransfer = method === 'card' && !fullyCovered;
  // Coins alone need no payment method; otherwise an online one must be chosen.
  const canPay = fullyCovered || (method !== null && isOnlineMethod(method));

  const handlePay = async () => {
    // The server ignores the method when coins cover everything, but the request still names one.
    const payWith = method && isOnlineMethod(method) ? method : 'payme';
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await entitlementsApi.CreateCheckout({
        planKey: plan.key,
        billingCycle: 'monthly',
        method: payWith,
        applyCoins,
        promocode: promocode.trim() || undefined,
        referralCode: referralCode.trim() || undefined,
      });
      window.location.href = data.checkoutUrl;
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('checkout_failed');
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={`${t('upgrade_to')} ${plan.displayName}`} size="sm">
      <div className="space-y-5">
        {/* Monthly price */}
        <div className="flex items-baseline justify-between rounded-lg border border-border p-3">
          <span className="text-sm font-medium text-foreground">{t('cycle_monthly')}</span>
          <span className="text-sm font-semibold text-foreground">
            {formatSom(plan.monthlyPrice)}
          </span>
        </div>

        {!cardTransfer && (
          <>
            {/* Promocode */}
            <div>
              <p className="mb-2 text-sm font-medium text-foreground">
                {t('promocode_label', 'Promo code')}
              </p>
              <input
                type="text"
                value={promocode}
                onChange={(e) => setPromocode(e.target.value.toUpperCase())}
                placeholder={t('promocode_placeholder', 'Enter a code (optional)')}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase outline-none focus-visible:!outline-none focus:border-foreground/40 focus:ring-1 focus:ring-ring"
              />
            </div>

            {/* Referral code: referrals are examing.uz only. */}
            {!IS_IO && <div>
              <p className="mb-2 text-sm font-medium text-foreground">
                {t('referral_code_label', 'Referral code')}
              </p>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder={t('referral_code_placeholder', 'Have a referral code? (optional)')}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase outline-none focus-visible:!outline-none focus:border-foreground/40 focus:ring-1 focus:ring-ring"
              />
            </div>}
          </>
        )}

        {/* Apply coins: the coin wallet is examing.uz only. */}
        {!IS_IO && <label className="flex cursor-pointer items-center justify-between rounded-md border border-border p-3">
          <span className="text-sm text-foreground">
            {t('apply_coins', 'Apply coins')}
            <span className="ml-1 text-xs text-muted-foreground">
              ({formatSom(balance)} {t('available', 'available')})
            </span>
          </span>
          <Checkbox
            checked={applyCoins}
            disabled={balance <= 0}
            onCheckedChange={(checked) => setApplyCoins(checked === true)}
          />
        </label>}

        {!fullyCovered && (
          <PaymentMethodPicker
            methods={methods.methods}
            value={method}
            onChange={setMethod}
            isLoading={methods.isLoading}
          />
        )}

        {/* Summary */}
        {coinsApplicable > 0 && !cardTransfer && (
          <div className="space-y-1 rounded-lg bg-muted/50 p-3 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>{t('subtotal', 'Subtotal')}</span>
              <span>{formatSom(gross)}</span>
            </div>
            <div className="flex justify-between text-emerald-600">
              <span>{t('coins_discount', 'Coins')}</span>
              <span>−{formatSom(coinsApplicable)}</span>
            </div>
            <div className="flex justify-between font-medium text-foreground">
              <span>{t('total', 'Total')}</span>
              <span>{fullyCovered ? t('free', 'Free') : formatSom(charged)}</span>
            </div>
            {fullyCovered && (
              <p className="pt-1 text-xs text-emerald-600">
                {t('coins_cover_full', 'Your coins cover the full amount — no payment needed.')}
              </p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        {cardTransfer ? (
          <CardTransferPanel purpose={`${plan.displayName} (1 oy)`} amountSom={gross} />
        ) : (
          <Button className="w-full" onClick={handlePay} disabled={submitting || !canPay}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {fullyCovered
              ? t('subscribe_with_coins', 'Subscribe with coins')
              : `${t('pay_now')} · ${formatSom(charged)}`}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default CheckoutModal;
